import { EditOutlined } from '@ant-design/icons';
import { ExtendedDataProvider } from "@src/types";
import { Button, Card, Modal } from "antd";
import React, { useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import ReactJson from "@microlink/react-json-view";
import { isVisibleCreateServiceButton } from './utils';
import SchemaMonacoEditor from '@src/components/Services/SubComponents/SchemaMonacoEditor';
import { validateSchema } from './utils/schemaValidation';
import SchemaValidationErrors from './utils/SchemaValidationErrors';

interface ServiceRowSchemaTabProps {
    record: Record<string, any>;
}

const ServiceRowSchemaTab: React.FC<ServiceRowSchemaTabProps> = ({ record }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const [isSchemaModalVisible, setIsSchemaModalVisible] = useState(false);
    const [schemaValue, setSchemaValue] = useState('');
    const [isValidated, setIsValidated] = useState(false);
    const [validationErrors, setValidationErrors] = useState<any>(null);
    const refresh = useRefresh();

    const handleEditorChange = (value: string | undefined) => {
        setSchemaValue(value || '');
        setIsValidated(false);
        setValidationErrors(null);
    };

    const handleValidate = async () => {
        if (!schemaValue) return;
        const result = await validateSchema(schemaValue, dataProvider);
        if (result.success) {
            notify(result.message || 'Schema validation successful', 'success');
            setValidationErrors(null);
        } else {
            setValidationErrors(result.errors);
            notify(result.message || 'Schema validation failed', 'error');
        }
        setIsValidated(result.success);
    };

    const handleUpdateSchema = async () => {
        try {
            await dataProvider.create('services', {
                data: JSON.parse(schemaValue),
            });
            notify('Schema updated successfully', 'success');
            setIsSchemaModalVisible(false);
            refresh();
        } catch (error: any) {
            notify('Failed to update schema: ' + error.message, 'error');
        }
    };

    const showSchemaModal = () => {
        setSchemaValue(JSON.stringify(record.schema.schema, null, 2));
        setIsValidated(false);
        setValidationErrors(null);
        setIsSchemaModalVisible(true);
    };

    return (
        <Card extra={
            isVisibleCreateServiceButton() && <Button
                onClick={showSchemaModal}
                icon={<EditOutlined />} 
                style={{ marginBottom: '16px' }}
            >
                Edit Schema
            </Button>}>

            <ReactJson
                style={{ padding: '20px' }}
                src={record.schema.schema}
                displayDataTypes={false}
                name="schema"
                displayArrayKey={false}
                displayObjectSize={false}
            />
            <Modal
                title="Edit Schema"
                open={isSchemaModalVisible}
                onCancel={() => setIsSchemaModalVisible(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsSchemaModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button key="validate" onClick={handleValidate}>
                        Validate Schema
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleUpdateSchema}
                        disabled={!isValidated}
                    >
                        Update Schema
                    </Button>
                ]}
            >
                {validationErrors && (
                    <div style={{ marginTop: '16px' }}>
                        <SchemaValidationErrors errors={validationErrors} />
                    </div>
                )}
                <SchemaMonacoEditor
                    value={schemaValue}
                    onChange={handleEditorChange}
                />
         
            </Modal>
        </Card>
    );
};

export default ServiceRowSchemaTab; 